import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from 'src/environments/environment';
import {
  Assumptions,
  ImportData,
  UploadDataStatus,
  UserInfo,
} from '../models/UserInfo';
import { BehaviorSubject, filter, map, take, tap } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userInfoSubject: BehaviorSubject<UserInfo[]> = new BehaviorSubject(
    <UserInfo[]>{}
  );
  public assumptionsSubject: BehaviorSubject<Assumptions> = new BehaviorSubject(
    <Assumptions>{}
  );

  public uploadDataStatusSubject: BehaviorSubject<UploadDataStatus[]> =
    new BehaviorSubject(<UploadDataStatus[]>{});

  private impersonateSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public userInfo$: Observable<UserInfo[]> =
    this.userInfoSubject.asObservable();

  public currentUserSubject: BehaviorSubject<ImportData> = new BehaviorSubject(
    <ImportData>{}
  );

  public currentUser$: Observable<ImportData> =
    this.currentUserSubject.asObservable();

  public uploadDataStatus$: Observable<UploadDataStatus[]> =
    this.uploadDataStatusSubject.asObservable();

  public assumptions$: Observable<Assumptions> =
    this.assumptionsSubject.asObservable();

  public impersonate$: Observable<boolean> =
    this.impersonateSubject.asObservable();

  constructor(private http: HttpClient, public route: Router) {}

  getMembers(): Observable<UserInfo[]> {
    return this.http
      .get<UserInfo[]>(`${environment.apiBaseUrlPrefix}/UserInfo/memberinfo`)
      .pipe(tap((allUsers) => this.setUserInfo(allUsers)));
  }

  setCurrentUser(username: string): void {
    const url = `${
      environment.apiBaseUrlPrefix
    }/UserInfo/member?username=${encodeURIComponent(username)}`;

    this.http
      .get<ImportData>(url)
      .pipe(take(1))
      .subscribe((currentUser) => {
        if (currentUser === null){
          this.route.navigate(['/invalid']);
        }
        this.currentUserSubject.next(currentUser);

        return currentUser;
      });
  }

  getCurrentUserOb(username: string): Observable<ImportData> {
    const url = `${environment.apiBaseUrlPrefix}/UserInfo/member?username=${encodeURIComponent(username)}`;

    return this.http.get<ImportData>(url).pipe(
      tap((currentUser: ImportData) => {
        this.currentUserSubject.next(currentUser);
      })
    );
  }

  getAssumptions(): Observable<Assumptions> {
    return this.http
      .get<Assumptions>(`${environment.apiBaseUrlPrefix}/Assumptions`)
      .pipe(tap((assumptions) => this.setAssumptions(assumptions)));
  }

  getAllDataUploadStatus(): Observable<UploadDataStatus[]> {
    return this.http
      .get<UploadDataStatus[]>(`${environment.apiBaseUrlPrefix}/DataUpload`)
      .pipe(
        tap((allUploadDataStatus) =>
          this.setAllUploadDataStatus(allUploadDataStatus)
        )
      );
  }

  getProjectionResults(username: string): Observable<any> {
    const url = `${
      environment.apiBaseUrlPrefix
    }/Projection?username=${encodeURIComponent(username)}`;
    return this.http.get(url);
  }

  toggleLock(memberId: number): Observable<string> {
    return this.http.post<string>(
      `${environment.apiBaseUrlPrefix}/UserInfo/ToggleLock/${memberId}`,
      null
    );
  }

  updateMemberUsername(userInfo: UserInfo): Observable<string> {
    return this.http.post<string>(
      `${environment.apiBaseUrlPrefix}/UserInfo/UpdateMemberUsername`,
      userInfo
    );
  }

  insertDataUploadStatus(
    dataUploadStatus: UploadDataStatus
  ): Observable<string> {
    return this.http.post<string>(
      `${environment.apiBaseUrlPrefix}/DataUpload/InsertDataUploadStatus`,
      dataUploadStatus
    );
  }

  upsertMemberInfo(memberImportData: ImportData[]): Observable<string> {
    return this.http.post<string>(
      `${environment.apiBaseUrlPrefix}/UserInfo/UpsertMemberInfo`,
      memberImportData
    );
  }

  insertAssumptionsSettings(assumptions: Assumptions) {
    return this.http.post<string>(
      `${environment.apiBaseUrlPrefix}/Assumptions/InsertAssumptionsSettings`,
      assumptions
    );
  }

  getCurrentUser(): ImportData {
    return this.currentUserSubject.value;
  }

  getImpersonate(): boolean {
    return this.impersonateSubject.value;
  }

  public toggleImpersonate(user: string, state: boolean): void {
    const url = `${
      environment.apiBaseUrlPrefix
    }/UserInfo/member?username=${encodeURIComponent(user)}`;

    this.http
      .get<ImportData>(url)
      .pipe(take(1))
      .subscribe((currentUser) => {
        currentUser.IsAdmin = true;
        this.currentUserSubject.next(currentUser);
        
        this.impersonateSubject.next(state);
        return currentUser;
      });
  }

  public setUserInfo(allUsers: UserInfo[]) {
    this.userInfoSubject.next(allUsers);
  }

  public setAssumptions(assumptions: Assumptions) {
    this.assumptionsSubject.next(assumptions);
  }

  public setAllUploadDataStatus(allUploadDataStatus: UploadDataStatus[]) {
    this.uploadDataStatusSubject.next(allUploadDataStatus);
  }

  getSingleUserInfo(username: string): Observable<UserInfo | undefined> {
    return this.getMembers().pipe(
      map((user) => user.find((u) => u.username === username))
    );
  }
}
